// External library imports
import { Link } from "@tanstack/react-router";
import {
  Plus,
  LayoutDashboard,
  ListTodo,
  Shield,
  LucideIcon,
} from "lucide-react";

// UI Component imports
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

// Types
interface FeatureCard {
  icon: LucideIcon;
  title: string;
  description: string;
}

interface StatisticCard {
  icon: LucideIcon;
  title: string;
  height: string;
}

// Constants
const FEATURE_CARDS: FeatureCard[] = [
  {
    icon: Shield,
    title: "安全访问",
    description:
      "你的仪表盘采用邮箱与密码认证保护，确保数据隐私与安全。",
  },
  {
    icon: ListTodo,
    title: "待办管理",
    description:
      "高效创建、组织与跟踪待办。使用上方快捷按钮即可开始。",
  },
  {
    icon: LayoutDashboard,
    title: "资料设置",
    description:
      "自定义用户资料，管理账号偏好并更新个人信息。",
  },
];


/**
 * Renders a feature card component with icon, title, and description
 */
const FeatureCard = ({ icon: Icon, title, description }: FeatureCard) => (
  <Card className="border-neutral-100">
    <CardHeader>
      <CardTitle className="flex items-center text-lg text-neutral-950">
        <Icon className="mr-2 h-5 w-5 text-neutral-600" aria-hidden="true" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <p className="text-sm text-muted-foreground">{description}</p>
    </CardContent>
  </Card>
);

/**
 * Renders a statistic card component with placeholder content
 */
const StatisticCard = ({ icon: Icon, title, height }: StatisticCard) => (
  <Card className="border-neutral-100">
    <CardHeader>
      <CardTitle className="flex items-center text-lg text-neutral-950">
        <Icon className="mr-2 h-5 w-5 text-neutral-600" aria-hidden="true" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div
        className={`flex ${height} items-center justify-center rounded-lg bg-neutral-50`}
        role="img"
        aria-label={`${title} 可视化`}
      >
        <div className="space-y-2 text-center">
          <Icon
            className="mx-auto h-8 w-8 text-neutral-300"
            aria-hidden="true"
          />
          <p className="text-sm text-muted-foreground">
            {title} 将在此显示
          </p>
        </div>
      </div>
    </CardContent>
  </Card>
);

/**
 * DashboardPage Component
 * Main dashboard view displaying welcome message, quick actions, stats overview, and features
 */
const DashboardPage = () => {
  return (
    <div className="flex flex-col gap-6 p-4">
      {/* Header Section */}
  <header className="space-y-2">
    <h1 className="text-3xl font-bold tracking-tight">
      欢迎回来。
    </h1>
    <p className="text-muted-foreground">
      在同一处管理待办并查看应用统计。
    </p>
  </header>

      {/* Quick Actions */}
  <nav className="flex gap-4" aria-label="快捷操作">
        <Link to="/dashboard/todos/new">
      <Button
        className="bg-neutral-950 text-white hover:bg-neutral-800"
        aria-label="创建新待办"
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        创建待办
      </Button>
        </Link>
        <Link to="/dashboard/todos">
      <Button
        variant="outline"
        className="border-neutral-200"
        aria-label="查看所有待办"
      >
        <ListTodo className="h-4 w-4" aria-hidden="true" />
        查看待办
      </Button>
        </Link>
      </nav>

      {/* Feature Cards */}
  <section
    className="grid gap-4 md:grid-cols-3"
    aria-label="仪表盘功能"
  >
        {FEATURE_CARDS.map((card) => (
          <FeatureCard key={card.title} {...card} />
        ))}
      </section>

      {/* Statistics Section */}
  
    </div>
  );
};

export default DashboardPage;
